import {
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Icon, Separator, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import { Dropdown, type DropdownOption } from '~/components/Dropdown';
import { chevron } from '~/components/styles';
import { cn } from '~/lib/cn';
import type { VintageFilter } from '~/modules/label-perf/api/label-perf';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/label-perf/components/Table';
import { SkeletonTable } from '~/modules/label-perf/components/Table/SkeletonTable';

import { TopBlock } from './components/TopBlock';
import { DataUnavailable } from './DataUnavailable/DataUnavailable';
import { getReleaseOptions } from './releaseOptions';
import { useTrackPerformanceQuery } from './useTrackPerformanceQuery';
import { useTrackPerformanceTableColumns } from './useTrackPerformanceTableColumns';

const getVintageFilter = (releaseFilter: string): VintageFilter => {
  switch (releaseFilter) {
    case 'new':
      return 'NEW_RELEASE';
    case 'shallow':
      return 'SHALLOW_CATALOG';
    case 'deep':
      return 'DEEP_CATALOG';
    default:
      return undefined;
  }
};

const INITIAL_ITEMS_COUNT = 10;
const TOTAL_ITEMS_COUNT = 25;

const customScrollClassName = 'custom-scroll';

const getInitialSort = (searchParams: URLSearchParams): SortingState => {
  const sort = searchParams.get('sort');
  const order = searchParams.get('order');

  if (sort) {
    return [{ id: sort, desc: order === 'desc' }];
  }

  return [{ id: 'streamsTw', desc: true }];
};

interface TrackPerformanceProps {
  majorLabels?: string[];
  wmg1Labels?: number[];
  mixOneEnrich?: boolean;
}

export function TrackPerformance({
  majorLabels = [],
  wmg1Labels,
  mixOneEnrich,
}: TrackPerformanceProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('label-perf');

  const [streamFilter, setStreamFilter] = useState<'total' | 'platform'>(
    searchParams.get('streams') === 'platform' ? 'platform' : 'total',
  );
  const [releaseFilter, setReleaseFilter] = useState(
    searchParams.get('release') || 'all',
  );
  const [showAll, setShowAll] = useState(
    searchParams.get('limit') === String(TOTAL_ITEMS_COUNT),
  );
  const [columnSort, setColumnSort] = useState<SortingState>(() =>
    getInitialSort(searchParams),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const releaseOptions = getReleaseOptions(t);

  const streamOptions: DropdownOption[] = [
    { label: t('table.dropdown.streams.options.totalStreams'), value: 'total' },
    {
      label: t('table.dropdown.streams.options.streamsByPlatform'),
      value: 'platform',
    },
  ];

  const activeSort = useMemo(() => columnSort[0], [columnSort]);
  const vintage = useMemo(
    () => getVintageFilter(releaseFilter),
    [releaseFilter],
  );

  const {
    data = [],
    isError,
    isFetching,
  } = useTrackPerformanceQuery({
    majorLabels,
    wmg1Labels,
    pageNumber: 1,
    limit: TOTAL_ITEMS_COUNT,
    vintage,
    columnSort: [activeSort],
    mixOneEnrich,
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (releaseFilter !== 'all') {
      params.set('release', releaseFilter);
    } else {
      params.delete('release');
    }

    if (streamFilter !== 'total') {
      params.set('streams', streamFilter);
    } else {
      params.delete('streams');
    }

    if (showAll) {
      params.set('limit', String(TOTAL_ITEMS_COUNT));
    } else {
      params.delete('limit');
    }

    if (activeSort) {
      if (activeSort.id !== 'streamsTw' || !activeSort.desc) {
        params.set('sort', activeSort.id);
        params.set('order', activeSort.desc ? 'desc' : 'asc');
      } else {
        params.delete('sort');
        params.delete('order');
      }
    }

    setSearchParams(params, { replace: true });
  }, [
    releaseFilter,
    streamFilter,
    showAll,
    activeSort,
    searchParams,
    setSearchParams,
  ]);

  const columns = useTrackPerformanceTableColumns(streamFilter);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const pageSize = showAll ? TOTAL_ITEMS_COUNT : INITIAL_ITEMS_COUNT;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSortingRemoval: true,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: false,
    autoResetPageIndex: false,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: (updater) => {
      setColumnSort((old) => {
        const newSort = updater instanceof Function ? updater(old) : updater;

        if (newSort.length === 0) {
          const prevSortedCol = old[0]?.id;

          if (['streamsRtd', 'streamsTw'].includes(prevSortedCol)) {
            return old;
          }
        }

        // Handle a new sort state
        if (newSort.length > 0) {
          const nextSortCol = newSort[0];

          if (['streamsRtd', 'streamsTw'].includes(nextSortCol.id)) {
            return [{ id: nextSortCol.id, desc: true }];
          }
        }

        return newSort;
      });
    },
    state: {
      sorting: columnSort,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
  });

  if (isError) {
    return (
      <DataUnavailable
        title={t('error.failedLoading')}
        subtitle={t('error.refresh')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  const totalRecords = table.getCoreRowModel().rows.length;

  return (
    <div className="py-md">
      <div className="mb-lg flex items-center justify-between">
        <div>
          <p className="text-body-md font-bold">
            {t('body.trackPerformance')}{' '}
            <span className="font-normal text-subtle">({totalRecords})</span>
          </p>
          <p className="text-body-sm">{t('body.trackPerformanceDesc')}</p>
        </div>
        <div className="flex items-center gap-md">
          <p>{t('body.show')}</p>
          <div className="w-[230px]">
            <Dropdown
              onChange={(value) =>
                startTransition(() => {
                  setStreamFilter(value as 'total' | 'platform');
                })
              }
              contentClassName="z-10 text-body-sm"
              options={streamOptions}
              value={streamFilter}
            />
          </div>
          <div className="w-[230px]">
            <Dropdown
              onChange={(value) => setReleaseFilter(value)}
              contentClassName="z-10 text-body-sm"
              options={releaseOptions}
              value={releaseFilter}
              disabled={isFetching}
            />
          </div>
        </div>
      </div>

      {/* TOP 3 */}
      <div className="mb-2xl grid grid-cols-3 gap-md">
        <TopBlock index={1} block={data[0]} loading={isFetching} />
        <TopBlock index={2} block={data[1]} loading={isFetching} />
        <TopBlock index={3} block={data[2]} loading={isFetching} />
      </div>

      <div className={cn('relative overflow-x-auto', customScrollClassName)}>
        {/* Table Header (fixed) */}
        <Table
          aria-label={t('trackList.labels.aria')}
          className="w-full table-fixed"
        >
          <TableHeader className="bg-white sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'px-sm text-body-sm font-normal first:pl-lg',
                      header.column.columnDef.meta?.className,
                    )}
                    colSpan={header.colSpan}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
        </Table>

        {/* Table Body */}
        <div ref={tableBodyRef}>
          <Table
            aria-label={t('trackList.labels.aria') + ' body'}
            className="w-full table-fixed"
          >
            {isFetching ? (
              <TableBody>
                <SkeletonTable
                  size={INITIAL_ITEMS_COUNT}
                  headers={table.getHeaderGroups()[0].headers}
                  firstLineClassName="w-[3xl]"
                  secondLineClassName="w-2xl"
                  cellClassName="pl-lg"
                />
              </TableBody>
            ) : null}
            {!isFetching && table.getRowModel().rows.length === 0 && (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="py-lg text-center"
                  >
                    {t('noResults')}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            {!isFetching && table.getRowModel().rows.length > 0 && (
              <TableBody>
                {table.getRowModel().rows.map((row, index) => (
                  <Tooltip
                    key={row.id}
                    content={<></>}
                    delayDuration={1}
                    triggerAsChild
                    contentClassName="rounded-sm border-px p-md bg-level-03 border-emphasis-subtle"
                    side="bottom"
                    sideOffset={8}
                    align="center"
                    alignOffset={-16}
                    enabled={false}
                  >
                    <TableRow
                      aria-label={`Row for ${row.original?.familyId || 'track'}`}
                      data-state={row.getIsSelected() && 'selected'}
                      className="group/row relative transition-all duration-300 ease-out hover:bg-emphasis-subtle"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          scope="row"
                          className={cn(
                            'border-y p-0 transition-all duration-300 ease-out border-transparent last:py-xs',
                            'group-hover/row:border-emphasis-stronger',
                            'first:rounded-l-sm first:border-l',
                            'last:rounded-r-sm last:border-r last:pr-sm',
                            index % 2 === 0 && 'bg-default-inverse/default',
                            'first:pl-lg',
                            cell.column.columnDef.meta?.className,
                          )}
                        >
                          <div
                            className={cn('flex items-center gap-sm py-sm', {
                              'pl-lg': cell.column.id === 'title',
                              'justify-center':
                                streamFilter === 'platform' &&
                                ![
                                  'rowNumber',
                                  'familyTitle',
                                  'majorLabels',
                                  'vintages',
                                ].includes(cell.column.id),
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  </Tooltip>
                ))}
              </TableBody>
            )}
          </Table>
        </div>
      </div>

      {data.length ? (
        <div className="mt-lg flex items-center gap-md">
          <Separator className="w-full" />
          <Button
            variant="secondary"
            onClick={() => {
              setShowAll((prev) => {
                const nextValue = !prev;

                if (!nextValue) {
                  requestAnimationFrame(() => {
                    tableBodyRef.current?.scrollIntoView({
                      block: 'start',
                    });
                  });
                }

                return nextValue;
              });
            }}
            className="relative flex min-w-[9.5em] items-center gap-md"
          >
            <p>
              {!showAll
                ? t('buttons.showTop25', 'Show top 25')
                : t('buttons.showTop10', 'Show top 10')}
            </p>
            <Icon
              className={chevron({ state: showAll ? 'open' : 'default' })}
              size="md"
              type="chevron-down"
              variant="light"
            />
          </Button>
          <Separator className="w-full" />
        </div>
      ) : null}
    </div>
  );
}

TrackPerformance.displayName = 'TrackPerformance';
